import { useTranslate } from '@tolgee/react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { relocatePublishButtons } from '../../utils/uploadMediaFile';
import {
  ALL,
  FACEBOOK,
  PLATFORM_LIST,
  PLATFORM_LIST_NAMES
} from '../../../../config/constants';
import { useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import { PLATFORM_COLORS, PLATFORM_ICONS } from '../../../../config/icons';
import Carousel from './Carousel';
import Editor from './editor';
import VideoEditor from './videoEditor';
import SuccessToast from '../../cards/toast';
import { AddImageButton, EditorHeader } from '../sharedComponents';
import toast from 'react-hot-toast';
import { IMAGE, VIDEO } from '../../config/constants';
import {
  BRAND_OVERLAY_OPTIONS,
  platformFeatures,
  POST_TYPE_KEY
} from '../../config/platformFeatures';

interface Props {
  handleClose: Function;
  currentPlatform: string;
  currentIndex: any;
  cropMode: boolean;
}

const BrandImagesEditor: FC<Props> = ({
  handleClose,
  currentPlatform = ALL,
  currentIndex,
  cropMode
}) => {
  const { t } = useTranslate();
  const footerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [rightMargin, setRightMargin] = useState(0);
  const [startSubmission, setStartSubmission] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const triggerSuccessToast = (type) => {
    toast.success(
      type === IMAGE
        ? t('lomavisCreator.image.saved')
        : t('lomavisCreator.video.saved'),
      {
        position: 'top-right',
        id: 'accounts.invites.table.invite_deleted'
      }
    );
  };

  const typeKey = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postTypeSelected
  );
  const postTypeRule = (platform) => {
    return platformFeatures[platform][POST_TYPE_KEY]?.find(
      (pt) => pt.typeKey === typeKey[platform]
    );
  };

  const platformEnabled = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.platformEnabled
  );
  const allEnabledPlatforms = PLATFORM_LIST.filter(
    (item) => platformEnabled[item] === true
  );
  const [selectedPlatform, setSelectedPlatform] = useState(ALL);
  const postDataMediaFiles = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[selectedPlatform]?.media
  );
  const postData = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[selectedPlatform]
  );

  const [mediaFiles, setMediaFiles] = useState(
    postDataMediaFiles
      ?.filter(
        (file) => file.media_type === 'image' || file.media_type === 'video'
      )
      .map((file: any) => {
        return {
          id: `${file.id}`,
          uuid: file.uuid,
          name: file.name,
          preview_image: file.preview_image,
          orientation: file.orientation,
          media_type: file.media_type,
          video_duration: file.video_duration,
          uploadPreset: file.uploadPreset,
          file: file.file,
          file_type: file.file_type,
          uploaded: file.uploaded,
          assetBlob: file.assetBlob,
          editorUpdateState: file.editorUpdateState,
          source_media_uuid: file.source_media_uuid,
          key: `${new Date()}`,
          previewImageURL: file.previewImageURL,
          thumbnail_image: file.thumbnail_image,
          original_asset: file.original_asset,
          platform: currentPlatform,
          upload_to_platform_status: postData?.upload_to_platform_status,
          cropSuggestions:
            platformFeatures[BRAND_OVERLAY_OPTIONS]?.cropSuggestions,

          defaultCropRatio:
            platformFeatures[BRAND_OVERLAY_OPTIONS]?.defaultCropRatio,
          initialLoad: false
        };
      })
  );

  // const mediaFiles = postDataMediaFiles
  //   ?.filter(
  //     (file) => file.media_type === 'image' || file.media_type === 'video'
  //   )
  //   .map((file: any) => {
  //     return {
  //       id: `${file.id}`,
  //       uuid: file.uuid,
  //       name: file.name,
  //       preview_image: file.preview_image,
  //       orientation: file.orientation,
  //       media_type: file.media_type,
  //       video_duration: file.video_duration,
  //       uploadPreset: file.uploadPreset,
  //       file: file.file,
  //       file_type: file.file_type,
  //       uploaded: file.uploaded,
  //       assetBlob: file.assetBlob,
  //       editorUpdateState: file.editorUpdateState,
  //       source_media_uuid: file.source_media_uuid,
  //       key: `${new Date()}`,
  //       previewImageURL: file.previewImageURL,
  //       thumbnail_image: file.thumbnail_image,
  //       original_asset: file.original_asset,
  //       platform: platform,
  //       cropSuggestions:
  //         postTypeRule(platform)?.mediaTypes.image?.cropSuggestions,

  //       defaultCropRatio:
  //         postTypeRule(platform)?.mediaTypes.image?.defaultCropRatio
  //     };
  //   });

  const [selectedIndex, setSelectedIndex] = useState(currentIndex || 0);

  const updateCurrentCrop = (platform, newCropRatio) => {
    setMediaFiles((prevInfo) =>
      prevInfo.map((item) =>
        item.platform === platform
          ? { ...item, defaultCropRatio: newCropRatio, initialLoad: true }
          : item
      )
    );
  };

  useEffect(() => {
    relocatePublishButtons({
      footerRef: footerRef,
      setRightMargin: setRightMargin
    });
  }, []);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      // Skip the first render
      hasMounted.current = true;
      return;
    }

    // Only run this on subsequent changes
    setSelectedIndex(0);
  }, [selectedPlatform]);

  return (
    <div className="d-flex flex-column w-100 h-100 gap-4">
      <SuccessToast show={showToast} onClose={() => setShowToast(false)} />

      <EditorHeader>
        <div
          style={{}}
          className="d-flex justify-content-between align-items-center flex-fill h-100 main_editor"
        >
          <div className="h-100 w-100 d-flex justify-content-start align-items-center gap-3">
            {allEnabledPlatforms.map((platform) => (
              <i
                className={PLATFORM_ICONS[platform] + ' fs-1 me-2'}
                style={{
                  color: PLATFORM_COLORS[platform]
                }}
              ></i>
            ))}
          </div>
          <div
            style={{ flex: 1 }}
            className="d-flex justify-content-end align-item-center "
          ></div>
        </div>
      </EditorHeader>

      <div key={refetchKey} className="d-flex w-100 h-100">
        {mediaFiles[selectedIndex]?.media_type === IMAGE ? (
          <Editor
            mediaFiles={mediaFiles}
            setSelectedIndex={setSelectedIndex}
            selectedIndex={selectedIndex}
            startSubmission={startSubmission}
            platform={selectedPlatform}
            footerRef={footerRef}
            rightMargin={rightMargin}
            setStartSubmission={setStartSubmission}
            handleClose={handleClose}
            fileInputRef={fileInputRef}
            successNotification={() => triggerSuccessToast(IMAGE)}
            cropMode={cropMode}
            updateCurrentCrop={updateCurrentCrop}
          />
        ) : mediaFiles[selectedIndex]?.media_type === VIDEO ? (
          <VideoEditor
            mediaFiles={mediaFiles}
            setSelectedIndex={setSelectedIndex}
            selectedIndex={selectedIndex}
            startSubmission={startSubmission}
            platform={selectedPlatform}
            footerRef={footerRef}
            rightMargin={rightMargin}
            setStartSubmission={setStartSubmission}
            handleClose={handleClose}
            fileInputRef={fileInputRef}
            successNotification={() => triggerSuccessToast(VIDEO)}
          />
        ) : (
          <div></div>
        )}
      </div>

      {/* <div
        ref={footerRef}
        style={{ bottom: "0", zIndex: 100 }}
        className="w-100 d-flex justify-content-end gap-3 align-items-center bg-white py-2 border-top h-60px bg-white"
      >
        {!startSubmission && (
          <Button onClick={() => {}} variant="secondary" size="sm">
            {t("back")}
          </Button>
        )}
        <Button
          style={{ marginRight: rightMargin + "px" }}
          variant="primary"
          size="sm"
          onClick={() => {}}
        >
          {startSubmission && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
          )}
          {startSubmission ? t("lomavis_creator.saving") : t("save")}
        </Button>
      </div> */}
    </div>
  );
};

export default BrandImagesEditor;
